import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { PUBLIC_ROUTES } from "@/constants/routes";
import {
  DestructiveSettingsRow,
  SettingsSectionCard,
  ToggleSettingsRow,
} from "@/features/profile/components/SettingsSectionCard";
import { useChangePassword } from "@/features/profile/hooks/useChangePassword";
import { useDeleteAccount } from "@/features/profile/hooks/useDeleteAccount";
import { useMyProfile } from "@/features/profile/hooks/useMyProfile";
import { useUpdateNotificationSettings } from "@/features/profile/hooks/useNotificationSettings";
import {
  changePasswordSchema,
  type ChangePasswordValues,
} from "@/features/profile/schemas/profile.schemas";
import { useTheme } from "@/hooks/useTheme";
import { ApiError } from "@/types/api";
import { PasswordField } from "@/features/identity/components/PasswordField";
import { queryClient } from "@/lib/query-client";
import { clearAccessToken } from "@/lib/token-storage";
import { useAppDispatch } from "@/store/hooks";
import { clearCredentials } from "@/store/auth/authSlice";

/**
 * docs/ui/routes.md `/settings`, content per
 * docs/architecture/claude/ui-architecture.md: "separate toggle rows for
 * Push/Email notifications, Dark Mode." Full Name/Email/Phone/Avatar
 * editing lives on `/profile` instead (already built) — the two pages'
 * documented content overlaps there, and duplicating an edit form neither
 * page needs twice isn't worth it. No Privacy section: there's no backing
 * data for one (no granular visibility model on `User`) — a section of
 * toggles with nothing real behind them would be worse than not having it.
 */
export function SettingsPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const profileQuery = useMyProfile();
  const updateNotificationSettingsMutation = useUpdateNotificationSettings();
  const changePasswordMutation = useChangePassword();
  const deleteAccountMutation = useDeleteAccount();
  const { themePreference, setThemePreference } = useTheme();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmNewPassword: "" },
  });

  function handleToggle(key: "push" | "email" | "marketing", value: boolean) {
    if (!profileQuery.data) return;
    updateNotificationSettingsMutation.mutate(
      { ...profileQuery.data.notificationSettings, [key]: value },
      {
        onError: (error) =>
          showToast.error(error instanceof ApiError ? error.message : "Couldn't update settings."),
      },
    );
  }

  function onChangePassword(values: ChangePasswordValues) {
    changePasswordMutation.mutate(
      { currentPassword: values.currentPassword, newPassword: values.newPassword },
      {
        onSuccess: () => {
          showToast.success("Password changed.");
          reset();
        },
        onError: (error) =>
          showToast.error(error instanceof ApiError ? error.message : "Couldn't change password."),
      },
    );
  }

  function handleDeleteAccount() {
    // Navigate away from this protected page *first* — `/settings` is
    // behind `RequireAuth`. Starting the navigate() call before touching
    // any local auth state isn't enough on its own, though: it only starts
    // an async transition, and `/settings` (with its own `useMyProfile`
    // query) can still be mid-unmount when the clearing below runs. If
    // `queryClient.clear()` triggers a refetch from that not-yet-unmounted
    // page, it fires with no access token, 401s, and `interceptors.ts`'s
    // `handleSessionExpired` redirects — but its own guard only checks
    // `location.pathname`, not the query string, so if `RequireAuth` beat
    // it to a client-side `/login?redirect=/settings` redirect a moment
    // earlier, that stale redirect param survives (confirmed empirically:
    // this exact sequence happened every time without the `setTimeout`
    // below). Deferring the clear to a macrotask lets React finish
    // committing the navigation first, so nothing on `/settings` is still
    // mounted (or mid-unmount) by the time credentials actually clear.
    navigate(PUBLIC_ROUTES.home, { replace: true });
    deleteAccountMutation.mutate(undefined, {
      onSuccess: () => {
        setTimeout(() => {
          clearAccessToken();
          dispatch(clearCredentials());
          queryClient.clear();
        }, 0);
        showToast.success("Your account has been deleted.");
      },
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Couldn't delete account.");
      },
    });
  }

  if (profileQuery.isPending || !profileQuery.data) {
    return (
      <div className="mx-auto max-w-[640px] px-4 py-8">
        <p className="text-body text-muted-foreground">Loading settings…</p>
      </div>
    );
  }

  const { notificationSettings } = profileQuery.data;

  return (
    <div className="mx-auto max-w-[640px] space-y-6 px-4 py-8">
      <h1 className="text-h1 text-foreground">Settings</h1>

      <SettingsSectionCard title="Notifications">
        <ToggleSettingsRow
          label="Push notifications"
          description="New messages, price drops, and account activity."
          value={notificationSettings.push}
          onChange={(value) => handleToggle("push", value)}
        />
        <ToggleSettingsRow
          label="Email notifications"
          description="Important updates sent to your email."
          value={notificationSettings.email}
          onChange={(value) => handleToggle("email", value)}
        />
        <ToggleSettingsRow
          label="Marketing emails"
          description="Tips, offers, and product news."
          value={notificationSettings.marketing}
          onChange={(value) => handleToggle("marketing", value)}
        />
      </SettingsSectionCard>

      <SettingsSectionCard title="Appearance">
        <div className="flex items-center justify-between gap-4 py-1">
          <div>
            <p className="text-body text-foreground">Theme</p>
            <p className="text-body-sm text-muted-foreground">
              Choose how BazaarX looks on this device.
            </p>
          </div>
          <Select value={themePreference} onValueChange={setThemePreference}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </SettingsSectionCard>

      <SettingsSectionCard title="Security">
        <form onSubmit={handleSubmit(onChangePassword)} noValidate className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="currentPassword">Current password</Label>
            <PasswordField id="currentPassword" autoComplete="current-password" {...register("currentPassword")} />
            {errors.currentPassword && (
              <p className="text-caption text-destructive" role="alert">
                {errors.currentPassword.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="newPassword">New password</Label>
            <PasswordField id="newPassword" autoComplete="new-password" {...register("newPassword")} />
            {errors.newPassword && (
              <p className="text-caption text-destructive" role="alert">
                {errors.newPassword.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmNewPassword">Confirm new password</Label>
            <PasswordField
              id="confirmNewPassword"
              autoComplete="new-password"
              {...register("confirmNewPassword")}
            />
            {errors.confirmNewPassword && (
              <p className="text-caption text-destructive" role="alert">
                {errors.confirmNewPassword.message}
              </p>
            )}
          </div>
          <Button type="submit" isLoading={changePasswordMutation.isPending}>
            Change password
          </Button>
        </form>
      </SettingsSectionCard>

      <SettingsSectionCard title="Danger zone">
        <DestructiveSettingsRow
          label="Delete account"
          description="Permanently delete your account. This can't be undone."
          actionLabel="Delete"
          onClick={() => setIsDeleteDialogOpen(true)}
        />
      </SettingsSectionCard>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete your account?"
        description="This permanently deletes your account. Your listings and messages will remain, but you won't be able to log in again. This can't be undone."
        confirmLabel="Delete account"
        isDestructive
        isLoading={deleteAccountMutation.isPending}
        onConfirm={handleDeleteAccount}
      />
    </div>
  );
}
